// Apple MusicKit-JS loader + personal-connect orchestration (web only).
//
// Security / scope:
// - The developer token is minted SERVER-SIDE and fetched per-session; the .p8 private
//   key never reaches the browser. We never sign tokens here.
// - authorize() returns a Music User Token that MusicKit keeps in the browser. We do NOT
//   read it, store it, or send it anywhere — the server only records that the user
//   connected. Disconnect calls unauthorize() so MusicKit drops that token locally.
// - Personal account + catalog only. No sponsored seats, billing, or embedded playback.
//
// MusicKit v3 is loaded from Apple's official CDN. This is the main web app (not a
// sandboxed artifact), so the external script is allowed.

import { getAppleMusicDeveloperToken, connectAppleMusic, disconnectAppleMusic } from '@/services/performanceService';

const MUSICKIT_SRC = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
const APP_NAME = 'KaziPeople';
const APP_BUILD = '1.0.0';

type MusicKitGlobal = any; // Apple provides no bundled types; kept intentionally loose.

let scriptPromise: Promise<MusicKitGlobal> | null = null;

/** Injects the MusicKit script once and resolves when the MusicKit global is ready. */
function loadMusicKit(): Promise<MusicKitGlobal> {
  const w = window as unknown as { MusicKit?: MusicKitGlobal };
  if (w.MusicKit) return Promise.resolve(w.MusicKit);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<MusicKitGlobal>((resolve, reject) => {
    const onReady = () => {
      if (w.MusicKit) resolve(w.MusicKit);
      else reject(new Error('Apple MusicKit failed to initialise'));
    };
    document.addEventListener('musickitloaded', onReady, { once: true });

    if (!document.getElementById('apple-musickit-js')) {
      const s = document.createElement('script');
      s.id = 'apple-musickit-js';
      s.src = MUSICKIT_SRC;
      s.async = true;
      s.crossOrigin = 'anonymous';
      s.onerror = () => reject(new Error('Could not load Apple MusicKit'));
      document.head.appendChild(s);
    }
  }).catch((e) => {
    scriptPromise = null; // allow a later retry
    throw e;
  });
  return scriptPromise;
}

/** Configures MusicKit with a freshly-minted server-side developer token. */
async function configuredInstance(): Promise<MusicKitGlobal> {
  const MusicKit = await loadMusicKit();
  const { token } = await getAppleMusicDeveloperToken();
  await MusicKit.configure({ developerToken: token, app: { name: APP_NAME, build: APP_BUILD } });
  return MusicKit.getInstance();
}

/**
 * Opens Apple's authorization sheet for the user's OWN account, then records the
 * connection server-side. Throws if the user cancels or MusicKit can't load.
 */
export async function connectAppleMusicAccount(): Promise<void> {
  const instance = await configuredInstance();
  await instance.authorize(); // Apple popup; Music User Token stays inside MusicKit.
  await connectAppleMusic();
}

/** Clears the server connection record and drops the local MusicKit authorization. */
export async function disconnectAppleMusicAccount(): Promise<void> {
  await disconnectAppleMusic();
  try {
    const w = window as unknown as { MusicKit?: MusicKitGlobal };
    const instance = w.MusicKit?.getInstance?.();
    if (instance?.isAuthorized) await instance.unauthorize();
  } catch {
    /* best-effort local cleanup; the server record is already cleared */
  }
}
